import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSchool } from '../../context/SchoolContext';
import type { SchoolCode } from '../../types';
import { fetchGrades, fetchExamGradeSummary, type GradeDto, type ExamGradeSummaryDto } from '../../api/axios';
import { GradingTabs, GradingTab } from './GradingTabs';
import { StatusBadge } from './StatusBadge';
import {
  formatGradeForContext,
  getGradeColorForContext,
  usesLetterGrading,
} from '../../utils/letterGrading';
import {
  formatGradingTerm,
  gradingTermSelectOptions,
  defaultGradingTermYears,
} from '../../utils/gradingTerm';
import { fetchSchoolSettings } from '../../api/axios';

export const StudentGradesSection: React.FC = () => {
  const { user } = useAuth();
  const { currentSchool } = useSchool();
  const studentForm = typeof user?.form === 'number' ? user.form : null;
  const school = (user?.school ?? currentSchool) as SchoolCode;

  const [activeTab, setActiveTab] = useState<GradingTab>('exam');
  const [currentTerm, setCurrentTerm] = useState(() =>
    formatGradingTerm(1, new Date().getFullYear())
  );

  useEffect(() => {
    fetchSchoolSettings(currentSchool).then(s => {
      setCurrentTerm(formatGradingTerm(s.current_term, Number(s.current_academic_year)));
    }).catch(() => {});
  }, [currentSchool]);
  const [grades, setGrades] = useState<GradeDto[]>([]);
  const [examSummary, setExamSummary] = useState<ExamGradeSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchGrades(
          undefined,
          undefined,
          currentTerm,
          undefined,
          undefined,
          school
        );
        if (!cancelled) setGrades(data);
      } catch (error) {
        console.error('Failed to load grades', error);
        if (!cancelled) setGrades([]);
      }
      try {
        const summary = await fetchExamGradeSummary(currentTerm);
        if (!cancelled) setExamSummary(summary);
      } catch {
        if (!cancelled) setExamSummary(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentTerm, school]);

  const isKbaForm34 = school === 'KBA' && studentForm != null && studentForm >= 3;

  // Filter grades based on active tab
  const getFilteredGrades = () => {
    if (activeTab === 'exam') {
      // Exam grades: no assignment_id, admin_published = true, matching term
      return grades.filter(
        (g) =>
          (g.assignment_id === null || g.assignment_id === undefined) &&
          g.term === currentTerm &&
          !!(g as any).admin_published
      );
    } else {
      // Assignment grades: has assignment_id, published = true
      return grades.filter(
        (g) =>
          g.assignment_id !== null &&
          g.assignment_id !== undefined &&
          !!(g as any).published
      );
    }
  };

  const filteredGrades = getFilteredGrades();
  const gradingTermOptions = gradingTermSelectOptions(defaultGradingTermYears());
  const letterMode = usesLetterGrading(school, studentForm);
  const pointsLabel = letterMode ? 'Grade' : 'Points';

  // Group exam grades by subject
  const examGradesBySubject = filteredGrades
    .filter((g) => !g.assignment_id)
    .reduce((acc, grade) => {
      const subjectName = (grade.subject?.name || 'Unknown Subject').toUpperCase().trim();
      if (!acc[subjectName]) {
        acc[subjectName] = [];
      }
      acc[subjectName].push(grade);
      return acc;
    }, {} as Record<string, GradeDto[]>);

  // Group assignment grades by subject
  const assignmentGradesBySubject = filteredGrades
    .filter((g) => g.assignment_id)
    .reduce((acc, grade) => {
      const subjectName = (grade.subject?.name || 'Unknown Subject').toUpperCase().trim();
      if (!acc[subjectName]) {
        acc[subjectName] = [];
      }
      acc[subjectName].push(grade);
      return acc;
    }, {} as Record<string, GradeDto[]>);

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#999' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
        <div>Loading your grades...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <h2
            style={{
              fontSize: 28,
              fontWeight: 700,
              marginBottom: 0,
              color: '#333',
            }}
          >
            📊 My Grades
          </h2>
          <div
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={() => setShowHelp(true)}
            onMouseLeave={() => setShowHelp(false)}
          >
            <button
              type="button"
              aria-label="Learn more about My Grades"
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                border: '1px solid #93c5fd',
                background: '#eff6ff',
                color: '#1d4ed8',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'help',
              }}
            >
              ?
            </button>
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                width: 320,
                maxWidth: '80vw',
                padding: '12px 14px',
                borderRadius: 10,
                border: '1px solid #bfdbfe',
                background: '#fff',
                boxShadow: '0 10px 25px rgba(15,23,42,0.15)',
                color: '#1f2937',
                fontSize: 13,
                zIndex: 30,
                opacity: showHelp ? 1 : 0,
                visibility: showHelp ? 'visible' : 'hidden',
                transform: showHelp ? 'translateY(0)' : 'translateY(-4px)',
                transition: 'opacity 0.16s ease, transform 0.16s ease',
                pointerEvents: 'none',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>How to use this page</div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Pick a term to load exam results for that period.</li>
                <li>Switch between Exam and Assignments tabs to compare performance.</li>
                <li>Published grades are final and ready for report card use.</li>
              </ul>
            </div>
          </div>
        </div>
        <p style={{ fontSize: 15, color: '#666', margin: 0 }}>
          View your exam results and assignment grades
        </p>
      </div>

      {/* Term Selector */}
      <div
        style={{
          background: '#fff',
          padding: 20,
          borderRadius: 8,
          border: '1px solid #e0e0e0',
          marginBottom: 24,
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, color: '#333' }}>Academic Term</div>
        <select
          value={currentTerm}
          onChange={(e) => setCurrentTerm(e.target.value)}
          style={{
            padding: '10px 20px',
            borderRadius: 6,
            border: '2px solid #e0e0e0',
            background: '#fff',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {gradingTermOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <GradingTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        examCount={Object.keys(examGradesBySubject).length}
        assignmentCount={Object.values(assignmentGradesBySubject).reduce(
          (sum, grades) => sum + grades.length,
          0
        )}
      />

      {/* Exam Grades (Report Card) */}
      {activeTab === 'exam' && (
        <>
          {Object.keys(examGradesBySubject).length === 0 ? (
            <div
              style={{
                padding: 60,
                textAlign: 'center',
                background: '#fff',
                borderRadius: 12,
                border: '2px dashed #e0e0e0',
              }}
            >
              <div style={{ fontSize: 64, marginBottom: 16 }}>📋</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                No Exam Grades Available
              </div>
              <div style={{ fontSize: 14, color: '#999' }}>
                Your exam grades for {currentTerm} will appear here once they are published by your teachers
                and approved by the admin.
              </div>
            </div>
          ) : (
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e0e0e0',
                overflow: 'visible',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              }}
            >
              <div className="tableScroll">
                {/* Header Row */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 100px minmax(160px, 1.4fr) 120px',
                    padding: '16px 24px',
                    background: '#374151',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    minWidth: 640,
                  }}
                >
                  <div>Subject</div>
                  <div style={{ textAlign: 'center' }}>Marks</div>
                  <div style={{ textAlign: 'center' }}>{pointsLabel}</div>
                  <div style={{ textAlign: 'center' }}>Status</div>
                </div>

                {/* Subject Rows */}
                {Object.entries(examGradesBySubject)
                  .sort(([subjectA], [subjectB]) => subjectA.localeCompare(subjectB))
                  .map(([subjectName, subjectGrades], index) => {
                    const latestGrade = subjectGrades[subjectGrades.length - 1];
                    const marks = latestGrade.marks;
                    const gradeColor = getGradeColorForContext(school, studentForm, marks);
                    const isEven = index % 2 === 0;

                    return (
                      <div
                        key={subjectName}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 100px minmax(160px, 1.4fr) 120px',
                          padding: '20px 24px',
                          alignItems: 'center',
                          background: isEven ? '#fafafa' : '#fff',
                          borderBottom: '1px solid #e0e0e0',
                          transition: 'background 0.2s',
                          minWidth: 640,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f0f7ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isEven ? '#fafafa' : '#fff';
                        }}
                      >
                        {/* Subject Name */}
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: gradeColor,
                              flexShrink: 0,
                            }}
                          />
                          {subjectName}
                        </div>

                        {/* Marks */}
                        <div
                          style={{
                            textAlign: 'center',
                            fontSize: 20,
                            fontWeight: 700,
                            color: gradeColor,
                          }}
                        >
                          {marks}%
                        </div>

                        {/* Points */}
                        <div style={{ textAlign: 'center' }}>
                          <div
                            style={{
                              display: 'inline-block',
                              padding: '8px 12px',
                              background: gradeColor,
                              color: '#fff',
                              borderRadius: 6,
                              fontSize: 13,
                              fontWeight: 700,
                              lineHeight: 1.35,
                              maxWidth: 220,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                          >
                            {formatGradeForContext(school, studentForm, marks, latestGrade.grade)}
                          </div>
                        </div>

                        {/* Status */}
                        <div style={{ textAlign: 'center' }}>
                          <StatusBadge status="published" size="small" />
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Summary Footer */}
              <div
                style={{
                  padding: '16px 24px',
                  background: '#f8f9fa',
                  borderTop: '2px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 12,
                }}
              >
                <div style={{ fontSize: 14, color: '#666', fontWeight: 600 }}>
                  Total Subjects: {Object.keys(examGradesBySubject).length}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: '#666',
                    fontWeight: 600,
                    textAlign: 'right',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 4,
                  }}
                >
                  {isKbaForm34 ? (
                    <>
                      <div>
                        Best-six points total:{' '}
                        {examSummary?.msce_best_six_points_sum != null
                          ? examSummary.msce_best_six_points_sum
                          : '—'}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#888' }}>
                        Lower total is better (MSCE points).
                      </div>
                    </>
                  ) : (
                    <div>
                      {examSummary?.class_position != null &&
                      examSummary.class_size > 0 ? (
                        <>
                          Class position: {examSummary.class_position} of{' '}
                          {examSummary.class_size}
                        </>
                      ) : (
                        <>Class position: not available yet</>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Assignment Grades */}
      {activeTab === 'assignments' && (
        <>
          {Object.keys(assignmentGradesBySubject).length === 0 ? (
            <div
              style={{
                padding: 60,
                textAlign: 'center',
                background: '#fff',
                borderRadius: 12,
                border: '2px dashed #e0e0e0',
              }}
            >
              <div style={{ fontSize: 64, marginBottom: 16 }}>📚</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                No Assignment Grades Available
              </div>
              <div style={{ fontSize: 14, color: '#999' }}>
                Your assignment grades will appear here once your teachers publish them.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {Object.entries(assignmentGradesBySubject)
                .sort(([subjectA], [subjectB]) => subjectA.localeCompare(subjectB))
                .map(([subjectName, subjectGrades]) => (
                  <div
                    key={subjectName}
                    style={{
                      background: '#fff',
                      borderRadius: 12,
                      border: '1px solid #e0e0e0',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}
                  >
                    {/* Subject Header */}
                    <div
                      style={{
                        padding: '16px 24px',
                        background: '#2563eb',
                        color: '#fff',
                      }}
                    >
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{subjectName}</div>
                      <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>
                        {subjectGrades.length}{' '}
                        {subjectGrades.length === 1 ? 'Assignment' : 'Assignments'}
                      </div>
                    </div>

                    {/* Assignment List */}
                    <div style={{ padding: 16 }}>
                      {subjectGrades.map((grade, idx) => {
                        const gradeColor = getGradeColorForContext(school, studentForm, grade.marks);

                        return (
                          <div
                            key={grade.id}
                            style={{
                              padding: 16,
                              background: idx % 2 === 0 ? '#fafafa' : '#fff',
                              borderRadius: 8,
                              marginBottom: idx < subjectGrades.length - 1 ? 12 : 0,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 12,
                            }}
                          >
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>
                                {grade.assignment?.title || 'Assignment'}
                              </div>
                              {grade.teacher_remarks && (
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: '#666',
                                    marginTop: 4,
                                    fontStyle: 'italic',
                                  }}
                                >
                                  "{grade.teacher_remarks}"
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                              <div
                                style={{
                                  fontSize: 24,
                                  fontWeight: 700,
                                  color: gradeColor,
                                }}
                              >
                                {grade.marks}%
                              </div>
                              <div
                                style={{
                                  padding: '6px 12px',
                                  background: gradeColor,
                                  color: '#fff',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  textAlign: 'center',
                                  lineHeight: 1.35,
                                  maxWidth: 200,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                }}
                              >
                                {formatGradeForContext(school, studentForm, grade.marks, grade.grade)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
